/* Supercollider — global interactions.
   Vanilla JS only. Everything degrades gracefully without it. */
(function () {
  'use strict';

  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* --- Scroll reveal ----------------------------------------------------- */
  function initReveal(scope) {
    var els = (scope || document).querySelectorAll('[data-animate], [data-headline]');
    if (!els.length) return;

    if (reducedMotion || !('IntersectionObserver' in window)) {
      els.forEach(function (el) { el.classList.add('is-visible'); });
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
    );

    els.forEach(function (el) {
      if (el.dataset.animateDelay) {
        el.style.setProperty('--animate-delay', el.dataset.animateDelay);
      }
      observer.observe(el);
    });
  }

  /* Stagger children of [data-stagger] containers */
  function initStagger(scope) {
    (scope || document).querySelectorAll('[data-stagger]').forEach(function (group) {
      Array.prototype.forEach.call(group.children, function (child, i) {
        var target = child.hasAttribute('data-animate') ? child : child.querySelector('[data-animate]');
        if (target) target.style.setProperty('--animate-delay', String(Math.min(i, 8)));
      });
    });
  }

  /* --- Animated counters -------------------------------------------------- */
  function animateCounter(el) {
    var end = parseFloat(el.dataset.counter);
    if (isNaN(end)) return;
    var decimals = (el.dataset.counter.split('.')[1] || '').length;
    var duration = 1600;
    var start = null;

    function frame(now) {
      if (!start) start = now;
      var progress = Math.min((now - start) / duration, 1);
      var eased = 1 - Math.pow(1 - progress, 3);
      el.textContent = (end * eased).toFixed(decimals);
      if (progress < 1) requestAnimationFrame(frame);
    }
    requestAnimationFrame(frame);
  }

  function initCounters(scope) {
    var counters = (scope || document).querySelectorAll('[data-counter]');
    if (!counters.length) return;

    if (reducedMotion || !('IntersectionObserver' in window)) {
      counters.forEach(function (el) { el.textContent = el.dataset.counter; });
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            animateCounter(entry.target);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.4 }
    );
    counters.forEach(function (el) { observer.observe(el); });
  }

  /* --- Sticky header ------------------------------------------------------- */
  function initHeader() {
    var wrapper = document.querySelector('.site-header-wrapper--sticky');
    if (!wrapper) return;

    var hideOnScroll = wrapper.hasAttribute('data-hide-on-scroll');
    var lastY = window.scrollY;
    var ticking = false;

    function onScroll() {
      var y = window.scrollY;
      wrapper.classList.toggle('header-stuck', y > 30);
      if (hideOnScroll && !wrapper.querySelector('.nav-dropdown[open]')) {
        if (y > lastY && y > 250) {
          wrapper.classList.add('header-hidden');
        } else {
          wrapper.classList.remove('header-hidden');
        }
      }
      lastY = y;
      ticking = false;
    }

    window.addEventListener('scroll', function () {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(onScroll);
      }
    }, { passive: true });
    onScroll();
  }

  /* --- Mobile menu drawer ---------------------------------------------------- */
  function initDrawer() {
    var drawer = document.getElementById('MenuDrawer');
    if (!drawer) return;

    var openBtn = document.querySelector('[data-drawer-open]');
    var lastFocus = null;

    function setOpen(open) {
      if (open) {
        lastFocus = document.activeElement;
        drawer.setAttribute('data-open', '');
        document.body.style.overflow = 'hidden';
        var firstLink = drawer.querySelector('a, button, summary');
        if (firstLink) firstLink.focus();
      } else {
        drawer.removeAttribute('data-open');
        document.body.style.overflow = '';
        if (lastFocus) lastFocus.focus();
      }
      if (openBtn) openBtn.setAttribute('aria-expanded', open ? 'true' : 'false');
    }

    if (openBtn) openBtn.addEventListener('click', function () { setOpen(true); });
    drawer.querySelectorAll('[data-drawer-close]').forEach(function (el) {
      el.addEventListener('click', function () { setOpen(false); });
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && drawer.hasAttribute('data-open')) setOpen(false);
    });
  }

  /* --- Desktop dropdowns: close on outside click / escape --------------------- */
  function initDropdowns() {
    document.addEventListener('click', function (e) {
      document.querySelectorAll('.nav-dropdown[open]').forEach(function (dd) {
        if (!dd.contains(e.target)) dd.removeAttribute('open');
      });
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') {
        document.querySelectorAll('.nav-dropdown[open]').forEach(function (dd) {
          dd.removeAttribute('open');
        });
      }
    });
  }

  /* --- Carousels ----------------------------------------------------------------- */
  function initCarousels(scope) {
    (scope || document).querySelectorAll('[data-carousel]').forEach(function (carousel) {
      var track = carousel.querySelector('.carousel__track');
      if (!track) return;

      carousel.querySelectorAll('[data-carousel-prev], [data-carousel-next]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          var slide = track.querySelector('.carousel__slide');
          var step = slide ? slide.getBoundingClientRect().width + 20 : track.clientWidth * 0.8;
          var dir = btn.hasAttribute('data-carousel-prev') ? -1 : 1;
          track.scrollBy({ left: step * dir, behavior: reducedMotion ? 'auto' : 'smooth' });
        });
      });
    });
  }

  /* --- Hero parallax ------------------------------------------------------------- */
  function initParallax() {
    if (reducedMotion) return;
    var medias = document.querySelectorAll('.hero__media--parallax');
    if (!medias.length) return;

    var ticking = false;
    function update() {
      medias.forEach(function (media) {
        var rect = media.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top > window.innerHeight) return;
        var progress = rect.top / window.innerHeight;
        var child = media.firstElementChild;
        if (child) child.style.transform = 'translateY(' + (progress * -10) + '%)';
      });
      ticking = false;
    }
    window.addEventListener('scroll', function () {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(update);
      }
    }, { passive: true });
    update();
  }

  /* --- Quantity inputs -------------------------------------------------------------- */
  function initQuantity(scope) {
    (scope || document).querySelectorAll('.quantity').forEach(function (qty) {
      var input = qty.querySelector('.quantity__input');
      if (!input) return;
      qty.querySelectorAll('.quantity__button').forEach(function (btn) {
        btn.addEventListener('click', function () {
          var value = parseInt(input.value, 10) || 1;
          var min = parseInt(input.min, 10) || 1;
          value += btn.hasAttribute('data-plus') ? 1 : -1;
          input.value = Math.max(value, min);
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });
    });
  }

  /* --- Marquee duplication (ensures seamless loop on wide screens) ------------------- */
  function initMarquees(scope) {
    (scope || document).querySelectorAll('.marquee__track').forEach(function (track) {
      var group = track.querySelector('.marquee__group');
      if (!group || track.querySelectorAll('.marquee__group').length > 1) return;
      var clone = group.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      track.appendChild(clone);
    });
  }

  /* --- Services grid with switcher ------------------------------------------------- */
  function initServiceSwitchers(scope) {
    (scope || document).querySelectorAll('[data-service-switcher]').forEach(function (root) {
      if (root.dataset.switcherReady) return;
      var tabs = Array.prototype.slice.call(root.querySelectorAll('[data-service-tab]'));
      var panels = Array.prototype.slice.call(root.querySelectorAll('[data-service-panel]'));
      var medias = Array.prototype.slice.call(root.querySelectorAll('[data-detail-media]'));
      if (!tabs.length) return;
      root.dataset.switcherReady = 'true';

      /* The bridge may only be drawn from a card on the grid's bottom row —
         anywhere else it would run straight through the card beneath it.
         Measured with offset* rather than bounding rects because the entrance
         animation transforms the cards, and a rect taken mid-reveal would
         report the wrong row. */
      function updateBridges() {
        var rows = [];
        var maxBottom = -Infinity;
        tabs.forEach(function (tab) {
          var wrap = tab.closest ? tab.closest('.service-card-wrap') : null;
          if (!wrap) return;
          var bottom = wrap.offsetTop + wrap.offsetHeight;
          rows.push({ wrap: wrap, bottom: bottom });
          if (bottom > maxBottom) maxBottom = bottom;
        });
        rows.forEach(function (row) {
          row.wrap.classList.toggle('has-bridge', row.bottom >= maxBottom - 2);
        });
        updatePaneCorners();
      }

      var pane = root.querySelector('.service-detail');
      var overlay = root.querySelector('.service-detail__overlay');
      var hasBaseMedia = !!root.querySelector('.service-detail__media-base');
      var matchCardColours = root.hasAttribute('data-detail-match');

      /* Copy the selected card's resolved colours onto the pane, so the pane
         and the bridge always read as that card extended downwards. Reading
         computed values picks up per-card schemes, hover/active schemes and
         custom gradients without having to re-derive any of them here. */
      var COLOUR_PROPS = [
        '--color-background',
        '--color-foreground',
        '--color-accent',
        '--color-button',
        '--color-button-text'
      ];

      function applyPaneColours(tab) {
        if (!pane || !matchCardColours || !tab) return;
        var cs = getComputedStyle(tab);
        COLOUR_PROPS.forEach(function (prop) {
          var value = cs.getPropertyValue(prop).trim();
          if (value) pane.style.setProperty(prop, value);
        });
        /* A per-card gradient wins over the scheme's flat background. */
        var cardGradient = cs.getPropertyValue('--card-gradient').trim();
        var background = cardGradient || cs.getPropertyValue('--gradient-background').trim();
        if (background) pane.style.setProperty('--gradient-background', background);
      }

      /* Where the bridge meets a rounded pane corner it would leave a notch,
         so square off whichever corner the active card sits above. */
      function updatePaneCorners() {
        if (!pane) return;
        pane.classList.remove('is-square-start', 'is-square-end');
        var activeWrap = root.querySelector('.service-card-wrap.is-active.has-bridge');
        if (!activeWrap) return;
        /* offset* again, so a sliding entrance animation cannot skew this. */
        var paneLeft = pane.offsetLeft;
        var paneRight = paneLeft + pane.offsetWidth;
        var wrapLeft = activeWrap.offsetLeft;
        var wrapRight = wrapLeft + activeWrap.offsetWidth;
        if (Math.abs(wrapLeft - paneLeft) < 2) pane.classList.add('is-square-start');
        if (Math.abs(wrapRight - paneRight) < 2) pane.classList.add('is-square-end');
      }

      function select(id, moveFocus) {
        tabs.forEach(function (tab) {
          var on = tab.dataset.serviceTab === id;
          tab.classList.toggle('is-active', on);
          tab.setAttribute('aria-selected', on ? 'true' : 'false');
          tab.tabIndex = on ? 0 : -1;
          var wrap = tab.closest ? tab.closest('.service-card-wrap') : null;
          if (wrap) wrap.classList.toggle('is-active', on);
          if (on && moveFocus) tab.focus();
        });
        panels.forEach(function (panel) {
          panel.classList.toggle('is-active', panel.dataset.servicePanel === id);
        });
        /* A card with no image of its own falls back to the base image. */
        medias.forEach(function (media) {
          media.classList.toggle('is-active', media.dataset.detailMedia === id);
        });
        /* Only darken when this card actually has an image behind the text. */
        if (overlay) {
          var showsMedia = hasBaseMedia || medias.some(function (m) {
            return m.dataset.detailMedia === id;
          });
          overlay.classList.toggle('is-visible', showsMedia);
        }

        updateBridges();

        var activeTab = tabs.filter(function (t) { return t.dataset.serviceTab === id; })[0];
        /* The card's own colours settle a frame after .is-active lands. */
        requestAnimationFrame(function () { applyPaneColours(activeTab); });
      }

      /* Delegated so it keeps working whatever sits between the click target
         and the card, and survives the theme editor re-rendering blocks. */
      root.addEventListener('click', function (e) {
        var tab = e.target.closest && e.target.closest('[data-service-tab]');
        if (tab && root.contains(tab)) {
          e.preventDefault();
          select(tab.dataset.serviceTab);
        }
      });

      tabs.forEach(function (tab, i) {
        tab.addEventListener('keydown', function (e) {
          if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
            e.preventDefault();
            select(tab.dataset.serviceTab);
            return;
          }
          var dir = 0;
          if (e.key === 'ArrowRight' || e.key === 'ArrowDown') dir = 1;
          if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') dir = -1;
          if (e.key === 'Home') dir = 'first';
          if (e.key === 'End') dir = 'last';
          if (!dir) return;
          e.preventDefault();
          var next;
          if (dir === 'first') next = tabs[0];
          else if (dir === 'last') next = tabs[tabs.length - 1];
          else next = tabs[(i + dir + tabs.length) % tabs.length];
          select(next.dataset.serviceTab, true);
        });
      });

      /* In the theme editor, selecting a card block shows its panel. */
      root.addEventListener('shopify:block:select', function (e) {
        var tab = e.target.querySelector
          ? e.target.querySelector('[data-service-tab]') || e.target.closest('[data-service-tab]')
          : null;
        if (tab) select(tab.dataset.serviceTab);
      });

      updateBridges();
      applyPaneColours(root.querySelector('[data-service-tab].is-active') || tabs[0]);
      /* Rows reflow at breakpoints and as fonts load, so re-measure. */
      var bridgeTimer;
      window.addEventListener('resize', function () {
        clearTimeout(bridgeTimer);
        bridgeTimer = setTimeout(updateBridges, 150);
      });
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(updateBridges).catch(function () {});
      }
    });
  }

  /* --- Variant pickers --------------------------------------------------------------- */
  /* Every variant's price is pre-rendered server-side and all but one hidden, so
     switching options is a matter of swapping which price is visible — no
     client-side money formatting, and no request. */
  function initVariantSelects(scope) {
    (scope || document).querySelectorAll('[data-variant-form]').forEach(function (root) {
      if (root.dataset.variantReady) return;

      var selects = Array.prototype.slice.call(root.querySelectorAll('[data-option-select]'));
      var json = root.querySelector('[data-variant-json]');
      var idInput = root.querySelector('[data-variant-id]');
      if (!selects.length || !json || !idInput) return;

      var variants;
      try {
        variants = JSON.parse(json.textContent);
      } catch (e) {
        return;
      }
      if (!variants || !variants.length) return;

      root.dataset.variantReady = 'true';

      var prices = Array.prototype.slice.call(root.querySelectorAll('[data-variant-price]'));
      var button = root.querySelector('[data-buy-button]');
      var buyLabel = button ? button.getAttribute('data-buy-label') || button.textContent.trim() : '';

      function update() {
        var chosen = selects.map(function (s) { return s.value; });
        var match = null;
        for (var i = 0; i < variants.length; i++) {
          var v = variants[i];
          var hit = true;
          for (var j = 0; j < chosen.length; j++) {
            if (v.options[j] !== chosen[j]) { hit = false; break; }
          }
          if (hit) { match = v; break; }
        }

        if (match) idInput.value = match.id;

        if (prices.length) {
          prices.forEach(function (el) {
            var show = match && String(match.id) === el.getAttribute('data-variant-price');
            el.hidden = !show;
          });
        }

        if (button) {
          var available = !!(match && match.available);
          button.disabled = !available;
          button.textContent = match ? (available ? buyLabel : 'Sold out') : 'Unavailable';
        }
      }

      selects.forEach(function (select) {
        select.addEventListener('change', update);
      });
      update();
    });
  }

  /* --- Related products ------------------------------------------------------------- */
  /* Fetched from the Section Rendering API as the section nears the viewport, so the
     product page never waits on recommendations. */
  function initRecommendations(scope) {
    (scope || document).querySelectorAll('[data-recommendations]').forEach(function (root) {
      var url = root.getAttribute('data-recommendations-url');
      if (!url || root.dataset.recommendationsReady || root.querySelector('.product-grid')) return;
      root.dataset.recommendationsReady = 'true';

      function load() {
        fetch(url)
          .then(function (res) { return res.ok ? res.text() : Promise.reject(res.status); })
          .then(function (html) {
            var parsed = new DOMParser().parseFromString(html, 'text/html');
            var fresh = parsed.querySelector('[data-recommendations]');
            if (!fresh || !fresh.querySelector('.product-grid')) return false;
            root.innerHTML = fresh.innerHTML;
            return true;
          })
          /* A store with nothing to recommend, or an offline visitor, is not an
             error worth shouting about — the section just stays empty. The catch
             is kept to the fetch so a real fault in initAll still surfaces. */
          .catch(function () { return false; })
          .then(function (swapped) { if (swapped) initAll(root); });
      }

      if (!('IntersectionObserver' in window)) { load(); return; }
      var observer = new IntersectionObserver(function (entries) {
        if (entries[0].isIntersecting) {
          observer.disconnect();
          load();
        }
      }, { rootMargin: '400px' });
      observer.observe(root);
    });
  }

  function initAll(scope) {
    initMarquees(scope);
    initStagger(scope);
    initReveal(scope);
    initCounters(scope);
    initCarousels(scope);
    initQuantity(scope);
    initServiceSwitchers(scope);
    initVariantSelects(scope);
    initRecommendations(scope);
  }

  document.addEventListener('DOMContentLoaded', function () {
    initAll(document);
    initHeader();
    initDrawer();
    initDropdowns();
    initParallax();
  });

  /* Re-run inside the theme editor when sections re-render */
  document.addEventListener('shopify:section:load', function (e) {
    initAll(e.target);
    initHeader();
    initDrawer();
  });
})();
